import { queryBlocksAgent } from '@/lib/blocks-ai-agent';
import { parseJsonResponse } from '@/modules/templates/services/ai/ai-utils';
import { buildContractCheckerPrompt } from '../lib/build-contract-checker-prompt';
import type {
  AnalysisResult,
  ClauseFinding,
  ContractRole,
  RiskLevel,
} from '../types/contract-analysis.types';

export interface AnalyzeContractInput {
  contractText: string;
  role: ContractRole;
  topics?: string;
  projectName?: string;
  signal?: AbortSignal;
}

const AGENT_ID = import.meta.env.VITE_CONTRACT_CHECKER_AGENT_ID;

const VALID_RISK_LEVELS: ReadonlySet<RiskLevel> = new Set(['high', 'medium', 'low']);

function coerceRisk(value: unknown): RiskLevel {
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    if (VALID_RISK_LEVELS.has(lower as RiskLevel)) return lower as RiskLevel;
  }
  return 'medium';
}

function coerceNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

function coerceString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function normalizeFindings(raw: unknown): ClauseFinding[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry, index) => {
      if (!entry || typeof entry !== 'object') return null;
      const obj = entry as Record<string, unknown>;
      const title = coerceString(obj.title);
      const original = coerceString(obj.original);
      const proposedChange = coerceString(obj.proposedChange);
      if (!title || !original || !proposedChange) return null;

      const finding: ClauseFinding = {
        id: coerceString(obj.id) ?? `finding-${index + 1}`,
        risk: coerceRisk(obj.risk),
        title,
        original,
        proposedChange,
      };
      const fullCounter = coerceString(obj.fullCounterClause);
      if (fullCounter) finding.fullCounterClause = fullCounter;
      const notes = coerceString(obj.notes);
      if (notes) finding.notes = notes;
      return finding;
    })
    .filter((f): f is ClauseFinding => f !== null);
}

function deriveCounts(findings: ClauseFinding[]) {
  let high = 0;
  let medium = 0;
  let low = 0;
  for (const f of findings) {
    if (f.risk === 'high') high++;
    else if (f.risk === 'medium') medium++;
    else if (f.risk === 'low') low++;
  }
  return { high, medium, low };
}

// Extract `matchedIds` from the analysis response, keeping only ids that
// correspond to a real finding (the AI must never invent ids).
function normalizeMatchedIds(raw: unknown, findings: ClauseFinding[]): string[] {
  if (!Array.isArray(raw)) return [];
  const known = new Set(findings.map((f) => f.id));
  const seen = new Set<string>();
  const out: string[] = [];
  for (const entry of raw) {
    const id = coerceString(entry);
    if (id && known.has(id) && !seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

function normalizeAnalysisResult(
  parsed: Record<string, unknown>,
  fallbackProjectName: string
): AnalysisResult {
  const findings = normalizeFindings(parsed.findings);
  const counts = deriveCounts(findings);

  const projectName =
    coerceString(parsed.projectName) ?? (fallbackProjectName.trim() || 'Untitled contract');

  return {
    projectName,
    findings,
    totalPoints: coerceNumber(parsed.totalPoints, findings.length),
    highRiskCount: coerceNumber(parsed.highRiskCount, counts.high),
    mediumRiskCount: coerceNumber(parsed.mediumRiskCount, counts.medium),
    lowRiskCount: coerceNumber(parsed.lowRiskCount, counts.low),
    matchedIds: normalizeMatchedIds(parsed.matchedIds, findings),
  };
}

export async function analyzeContract(input: AnalyzeContractInput): Promise<AnalysisResult> {
  if (!AGENT_ID) {
    throw new Error('VITE_CONTRACT_CHECKER_AGENT_ID is not configured');
  }

  const message = buildContractCheckerPrompt(input);
  const response = await queryBlocksAgent(AGENT_ID, message, input.signal);

  if (!response?.message) {
    throw new Error('AI returned an empty response');
  }

  let parsed: Record<string, unknown>;
  try {
    parsed = parseJsonResponse(response.message);
  } catch (error) {
    console.error('[contract-checker] Failed to parse AI response', response.message);
    throw error;
  }

  return normalizeAnalysisResult(parsed, input.projectName ?? '');
}
